import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPost, apiDel, api } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  Pause,
  RefreshCw,
  Zap,
  Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';

const fmtMoney = (cents) => {
  if (cents == null || isNaN(cents)) return '—';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const fmtPct = (n) => (n == null || isNaN(n) ? '—' : `${(n * 100).toFixed(1)}%`);
const fmtNum = (n) => (n == null || isNaN(n) ? '—' : Number(n).toLocaleString());

const CATEGORIES = ['meta_ads', 'google_ads', 'tiktok_ads', 'vendor', 'organic', 'referral', 'manual', 'other'];
const COST_MODES = [
  { value: 'free', label: 'Free / Organic' },
  { value: 'per_lead', label: 'Per-Lead Fixed' },
  { value: 'live_pull', label: 'Live API Pull' },
  { value: 'manual_total', label: 'Manual Total' },
];

export default function LeadRoi() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [windowDays, setWindowDays] = useState(30);

  // ── Queries ────────────────────────────────────────────────────────────
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['lead-roi-summary', windowDays],
    queryFn: () => apiGet('/lead-roi/summary', { windowDays }),
    refetchInterval: 60_000,
  });
  const { data: ranking } = useQuery({
    queryKey: ['lead-roi-ranking', windowDays],
    queryFn: () => apiGet('/lead-roi/ranking', { windowDays }),
  });
  const { data: sources } = useQuery({
    queryKey: ['lead-roi-sources'],
    queryFn: () => apiGet('/lead-roi/sources'),
  });
  const { data: recs } = useQuery({
    queryKey: ['lead-roi-recommendations'],
    queryFn: () => apiGet('/lead-roi/recommendations'),
    refetchInterval: 60_000,
  });
  const { data: rules } = useQuery({
    queryKey: ['lead-roi-rules'],
    queryFn: () => apiGet('/lead-roi/rules'),
  });

  // ── Mutations ──────────────────────────────────────────────────────────
  const runRollup = useMutation({
    mutationFn: () => apiPost('/lead-roi/rollup/run', { windowDays }),
    onSuccess: () => {
      toast.success('Rollup complete');
      qc.invalidateQueries({ queryKey: ['lead-roi-summary'] });
      qc.invalidateQueries({ queryKey: ['lead-roi-ranking'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const runRecs = useMutation({
    mutationFn: () => apiPost('/lead-roi/recommendations/run', {}),
    onSuccess: () => {
      toast.success('AI optimizer ran');
      qc.invalidateQueries({ queryKey: ['lead-roi-recommendations'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const evalRules = useMutation({
    mutationFn: () => apiPost('/lead-roi/rules/evaluate', {}),
    onSuccess: (r) => {
      toast.success(`Rules evaluated (${r?.fired || 0} fired)`);
      qc.invalidateQueries({ queryKey: ['lead-roi-rules'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const acceptRec = useMutation({
    mutationFn: (id) => apiPost(`/lead-roi/recommendations/${id}/accept`, {}),
    onSuccess: () => {
      toast.success('Accepted');
      qc.invalidateQueries({ queryKey: ['lead-roi-recommendations'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const dismissRec = useMutation({
    mutationFn: (id) => apiPost(`/lead-roi/recommendations/${id}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead-roi-recommendations'] }),
  });
  const patchSource = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/lead-roi/sources/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      toast.success('Source updated');
      qc.invalidateQueries({ queryKey: ['lead-roi-sources'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const totals = summary?.totals || {};
  const items = ranking?.items || [];
  const recItems = recs?.items || [];
  const sourceItems = sources?.items || [];

  return (
    <div className="space-y-4">
      <TopBar
        title="Lead ROI Engine"
        subtitle="Cross-source spend, attribution, AI recommendations · Owner-only"
      />

      {/* Owner-only callout */}
      <div className="panel p-3 border border-amber-500/30 bg-amber-500/5 flex items-start gap-2 text-sm">
        <Lock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-white/80">
          <span className="text-amber-300 font-medium">Owner-only surface.</span>{' '}
          Spend, ROI, and ad-adjustment recommendations are visible to admins/superadmins only.
          Agents do not see this page or its data.
        </div>
      </div>

      {/* Window picker + run buttons */}
      <div className="panel p-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-white/50">Window:</span>
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setWindowDays(d)}
            className={`px-3 py-1 rounded ${
              windowDays === d ? 'bg-blue-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {d}d
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => runRollup.mutate()}
          disabled={runRollup.isPending}
          className="px-3 py-1 rounded-sm bg-white/5 hover:bg-white/10 text-white/80 flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${runRollup.isPending ? 'animate-spin' : ''}`} />
          Recompute Rollup
        </button>
        <button
          onClick={() => runRecs.mutate()}
          disabled={runRecs.isPending}
          className="px-3 py-1 rounded-sm bg-purple-600/20 hover:bg-purple-600/30 text-purple-200 flex items-center gap-1"
        >
          <Sparkles className={`w-3.5 h-3.5 ${runRecs.isPending ? 'animate-spin' : ''}`} />
          Run AI Optimizer
        </button>
        <button
          onClick={() => evalRules.mutate()}
          disabled={evalRules.isPending}
          className="px-3 py-1 rounded-sm bg-white/5 hover:bg-white/10 text-white/80 flex items-center gap-1"
        >
          <Zap className={`w-3.5 h-3.5 ${evalRules.isPending ? 'animate-spin' : ''}`} />
          Evaluate Rules
        </button>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile icon={DollarSign} label="Spend" value={fmtMoney(totals.spendCents)} loading={loadingSummary} />
        <Tile icon={Target} label="Leads" value={fmtNum(totals.leads)} loading={loadingSummary} />
        <Tile icon={CheckCircle2} label="Sold" value={fmtNum(totals.sold)} loading={loadingSummary} />
        <Tile icon={TrendingUp} label="CPL" value={fmtMoney(totals.cplCents)} loading={loadingSummary} />
        <Tile icon={TrendingUp} label="CPS" value={fmtMoney(totals.cpsCents)} loading={loadingSummary} />
        <Tile icon={Sparkles} label="ROAS" value={totals.roas != null ? `${totals.roas.toFixed(2)}x` : '—'} loading={loadingSummary} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        {[
          ['overview', 'Source Ranking'],
          ['recs', `AI Recommendations${recItems.length ? ` (${recItems.length})` : ''}`],
          ['sources', 'Sources'],
          ['rules', 'Auto-Rules'],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 ${
              tab === k
                ? 'border-blue-500 text-white'
                : 'border-transparent text-white/60 hover:text-white/80'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab bodies */}
      {tab === 'overview' && (
        <RankingTable items={items} />
      )}

      {tab === 'recs' && (
        <RecsPanel items={recItems} onAccept={(id) => acceptRec.mutate(id)} onDismiss={(id) => dismissRec.mutate(id)} />
      )}

      {tab === 'sources' && (
        <SourcesPanel items={sourceItems} onPatch={(id, body) => patchSource.mutate({ id, body })} />
      )}

      {tab === 'rules' && (
        <RulesPanel rules={rules?.items || []} onRefresh={() => qc.invalidateQueries({ queryKey: ['lead-roi-rules'] })} />
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, loading }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center gap-2 text-white/50 text-xs">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-xl font-semibold text-white mt-1">
        {loading ? <span className="text-white/30">…</span> : value}
      </div>
    </div>
  );
}

function RankingTable({ items }) {
  if (!items.length) {
    return (
      <div className="panel p-8 text-center text-white/50 text-sm">
        No source performance data yet. New leads and spend will appear here after the next rollup.
      </div>
    );
  }
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-white/50 text-xs uppercase border-b border-white/10">
          <tr>
            <th className="text-left p-2">#</th>
            <th className="text-left p-2">Source</th>
            <th className="text-left p-2">Category</th>
            <th className="text-right p-2">Spend</th>
            <th className="text-right p-2">Leads</th>
            <th className="text-right p-2">Sold</th>
            <th className="text-right p-2">CPL</th>
            <th className="text-right p-2">CPS</th>
            <th className="text-right p-2">Sold %</th>
            <th className="text-right p-2">Score</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => {
            const score = r.score ?? r.roiScore ?? 0;
            return (
            <tr key={r.sourceId || i} className="border-b border-white/5 hover:bg-white/5">
              <td className="p-2 text-white/40">{i + 1}</td>
              <td className="p-2 text-white">{r.name || r.sourceName}</td>
              <td className="p-2 text-white/60 text-xs">{r.category}</td>
              <td className="p-2 text-right text-white/80">{fmtMoney(r.spendCents ?? r.spend)}</td>
              <td className="p-2 text-right text-white/80">{fmtNum(r.leads)}</td>
              <td className="p-2 text-right text-white/80">{fmtNum(r.sold)}</td>
              <td className="p-2 text-right text-white/80">{fmtMoney(r.cplCents ?? r.cpl)}</td>
              <td className="p-2 text-right text-white/80">{fmtMoney(r.cpsCents ?? r.cps)}</td>
              <td className="p-2 text-right text-white/80">{fmtPct(r.soldRate)}</td>
              <td className="p-2 text-right">
                <span
                  className={`px-1.5 py-0.5 rounded text-xs ${
                    score >= 70 ? 'bg-green-500/20 text-green-300' :
                    score >= 40 ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}
                >
                  {score ? Number(score).toFixed(0) : '—'}
                </span>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RecsPanel({ items, onAccept, onDismiss }) {
  if (!items.length) {
    return (
      <div className="panel p-8 text-center text-white/50 text-sm">
        No active recommendations. The optimizer runs nightly at 4am — or click <span className="text-purple-300">Run AI Optimizer</span> above.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r._id} className="panel p-3">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 p-1.5 rounded ${
              r.kind === 'pause' ? 'bg-red-500/20 text-red-300' :
              r.kind === 'scale' ? 'bg-green-500/20 text-green-300' :
              r.kind === 'reallocate' ? 'bg-blue-500/20 text-blue-300' :
              'bg-purple-500/20 text-purple-300'
            }`}>
              {r.kind === 'pause' ? <Pause className="w-3.5 h-3.5" /> :
               r.kind === 'scale' ? <TrendingUp className="w-3.5 h-3.5" /> :
               r.kind === 'reallocate' ? <Sparkles className="w-3.5 h-3.5" /> :
               <AlertTriangle className="w-3.5 h-3.5" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase text-white/40">{r.kind}</span>
                {r.confidence != null && (
                  <span className="text-xs text-white/40">· {(r.confidence * 100).toFixed(0)}% conf</span>
                )}
              </div>
              <div className="text-white text-sm mt-0.5">{r.title || r.summary}</div>
              {r.detail && <div className="text-white/60 text-xs mt-1">{r.detail}</div>}
              {r.metrics && (
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-white/50">
                  {r.metrics.spendCents != null && <span>Spend: {fmtMoney(r.metrics.spendCents)}</span>}
                  {r.metrics.leads != null && <span>Leads: {fmtNum(r.metrics.leads)}</span>}
                  {r.metrics.sold != null && <span>Sold: {fmtNum(r.metrics.sold)}</span>}
                  {r.metrics.cplCents != null && <span>CPL: {fmtMoney(r.metrics.cplCents)}</span>}
                  {r.metrics.cpsCents != null && <span>CPS: {fmtMoney(r.metrics.cpsCents)}</span>}
                </div>
              )}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onAccept(r._id)}
                className="px-2 py-1 rounded-sm bg-green-600/20 hover:bg-green-600/30 text-green-300 text-xs flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" /> Accept
              </button>
              <button
                onClick={() => onDismiss(r._id)}
                className="px-2 py-1 rounded-sm bg-white/5 hover:bg-white/10 text-white/60 text-xs flex items-center gap-1"
              >
                <XCircle className="w-3 h-3" /> Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SourcesPanel({ items, onPatch }) {
  if (!items.length) {
    return (
      <div className="panel p-8 text-center text-white/50 text-sm">
        No sources discovered yet. Sources auto-register the first time a lead arrives with attribution.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <SourceRow key={s._id} s={s} onPatch={onPatch} />
      ))}
    </div>
  );
}

function SourceRow({ s, onPatch }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: s.name || '',
    active: s.active !== false,
    costMode: s.costMode || 'per_lead',
    defaultCostPerLeadCents: s.defaultCostPerLeadCents || 0,
    manualSpendCents: s.manualSpendCents || 0,
    manualSpendPeriod: s.manualSpendPeriod || 'monthly',
    notes: s.notes || '',
  });
  return (
    <div className="panel p-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen(!open)}
          className="text-left flex-1"
        >
          <div className="flex items-center gap-2">
            <span className="text-white">{s.name}</span>
            <span className="text-xs text-white/40">{s.category}</span>
            {s.active === false && (
              <span className="text-xs px-1.5 py-0.5 rounded-sm bg-red-500/20 text-red-300">paused</span>
            )}
          </div>
          <div className="text-xs text-white/50 mt-0.5">
            30d: {fmtMoney(s.stats?.spendCents)} · {fmtNum(s.stats?.leads)} leads · {fmtNum(s.stats?.sold)} sold · CPS {fmtMoney(s.stats?.cpsCents)}
          </div>
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-white/10 grid md:grid-cols-2 gap-2 text-sm">
          <Field label="Display Name">
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="Cost Mode">
            <select
              className="input"
              value={form.costMode}
              onChange={(e) => setForm({ ...form, costMode: e.target.value })}
            >
              {COST_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          {form.costMode === 'per_lead' && (
            <Field label="Cost / Lead ($)">
              <input
                className="input"
                type="number"
                step="0.01"
                value={(form.defaultCostPerLeadCents / 100).toFixed(2)}
                onChange={(e) => setForm({ ...form, defaultCostPerLeadCents: Math.round(parseFloat(e.target.value || 0) * 100) })}
              />
            </Field>
          )}
          {form.costMode === 'manual_total' && (
            <>
              <Field label="Manual Spend ($)">
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={(form.manualSpendCents / 100).toFixed(2)}
                  onChange={(e) => setForm({ ...form, manualSpendCents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                />
              </Field>
              <Field label="Period">
                <select
                  className="input"
                  value={form.manualSpendPeriod}
                  onChange={(e) => setForm({ ...form, manualSpendPeriod: e.target.value })}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
            </>
          )}
          <Field label="Active">
            <label className="inline-flex items-center gap-2 text-white/80">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Tracking enabled
            </label>
          </Field>
          <Field label="Notes" full>
            <textarea
              className="input"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
          <div className="md:col-span-2 flex justify-end">
            <button
              onClick={() => onPatch(s._id, form)}
              className="px-3 py-1 rounded-sm bg-blue-600 hover:bg-blue-700 text-white text-sm"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RulesPanel({ rules, onRefresh }) {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const blank = {
    name: '',
    enabled: true,
    metric: 'cpsCents',
    op: 'gt',
    threshold: 10000,
    windowDays: 7,
    minLeadsInWindow: 20,
    scope: { category: '' },
    action: 'notify_only',
  };
  const [draft, setDraft] = useState(blank);

  async function create() {
    try {
      await apiPost('/lead-roi/rules', draft);
      toast.success('Rule created');
      setDraft(blank);
      setShowNew(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  }
  async function remove(id) {
    if (!confirm('Delete this rule?')) return;
    try {
      await apiDel(`/lead-roi/rules/${id}`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  }
  async function toggle(r) {
    try {
      await api.patch(`/lead-roi/rules/${r._id}`, { enabled: !r.enabled });
      onRefresh();
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="text-xs text-white/50">
          Rules evaluate nightly at 4:30am. Only Meta has server-side execution; others fire as alerts.
        </div>
        <button
          onClick={() => setShowNew(!showNew)}
          className="px-3 py-1 rounded-sm bg-blue-600 hover:bg-blue-700 text-white text-sm"
        >
          {showNew ? 'Cancel' : '+ New Rule'}
        </button>
      </div>

      {showNew && (
        <div className="panel p-3 grid md:grid-cols-3 gap-2 text-sm">
          <Field label="Name">
            <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field label="Category Scope">
            <select className="input" value={draft.scope.category} onChange={(e) => setDraft({ ...draft, scope: { ...draft.scope, category: e.target.value } })}>
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Action">
            <select className="input" value={draft.action} onChange={(e) => setDraft({ ...draft, action: e.target.value })}>
              <option value="notify_only">Notify only</option>
              <option value="pause">Pause source (Meta only)</option>
              <option value="scale_budget">Scale budget (Meta only)</option>
            </select>
          </Field>
          <Field label="Metric">
            <select className="input" value={draft.metric} onChange={(e) => setDraft({ ...draft, metric: e.target.value })}>
              <option value="cplCents">CPL</option>
              <option value="cpsCents">CPS</option>
              <option value="soldRate">Sold Rate</option>
              <option value="contactRate">Contact Rate</option>
              <option value="leads">Leads</option>
              <option value="spendCents">Spend</option>
            </select>
          </Field>
          <Field label="Operator">
            <select className="input" value={draft.op} onChange={(e) => setDraft({ ...draft, op: e.target.value })}>
              <option value="gt">&gt;</option>
              <option value="gte">&ge;</option>
              <option value="lt">&lt;</option>
              <option value="lte">&le;</option>
              <option value="eq">=</option>
            </select>
          </Field>
          <Field label="Threshold">
            <input className="input" type="number" value={draft.threshold} onChange={(e) => setDraft({ ...draft, threshold: Number(e.target.value) })} />
          </Field>
          <Field label="Window (days)">
            <input className="input" type="number" value={draft.windowDays} onChange={(e) => setDraft({ ...draft, windowDays: Number(e.target.value) })} />
          </Field>
          <Field label="Min Leads in Window">
            <input className="input" type="number" value={draft.minLeadsInWindow} onChange={(e) => setDraft({ ...draft, minLeadsInWindow: Number(e.target.value) })} />
          </Field>
          <div className="md:col-span-3 flex justify-end">
            <button onClick={create} className="px-3 py-1 rounded-sm bg-green-600 hover:bg-green-700 text-white text-sm">
              Create Rule
            </button>
          </div>
        </div>
      )}

      {!rules.length ? (
        <div className="panel p-8 text-center text-white/50 text-sm">
          No auto-rules defined. Add one above (e.g. "pause when CPS &gt; $100 and leads &ge; 20 in 7d").
        </div>
      ) : (
        rules.map((r) => (
          <div key={r._id} className="panel p-3 flex items-center gap-3 text-sm">
            <button
              onClick={() => toggle(r)}
              className={`px-2 py-1 rounded-sm text-xs ${r.enabled ? 'bg-green-500/20 text-green-300' : 'bg-white/5 text-white/40'}`}
            >
              {r.enabled ? 'ON' : 'OFF'}
            </button>
            <div className="flex-1">
              <div className="text-white">{r.name}</div>
              <div className="text-xs text-white/50">
                When {r.metric} {r.op} {r.threshold} (last {r.windowDays}d, min {r.minLeadsInWindow} leads
                {r.scope?.category ? `, ${r.scope.category}` : ''}) → {r.action}
              </div>
            </div>
            <button
              onClick={() => remove(r._id)}
              className="text-white/40 hover:text-red-300 text-xs"
            >
              Delete
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? 'md:col-span-2' : ''}`}>
      <div className="text-xs text-white/50 mb-1">{label}</div>
      {children}
    </label>
  );
}
